import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MAX_GALLERY_IMAGES } from '../constants/galleryEntityTypes';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
  },
  '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
};

const EntityGalleryFormSection = ({ value, onChange, disabled = false }) => {
  const [fileInputKey, setFileInputKey] = useState(0);

  const setField = (patch) => onChange({ ...value, ...patch });

  const handleFilesSelected = (fileList) => {
    const selected = Array.from(fileList || []);
    if (!selected.length) return;
    const combined = [...(value.files || []), ...selected].slice(0, MAX_GALLERY_IMAGES);
    if (combined.length >= MAX_GALLERY_IMAGES && (value.files?.length || 0) + selected.length > MAX_GALLERY_IMAGES) {
      // still cap silently; parent may show toast
    }
    Promise.all(
      combined.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve({ name: file.name, src: reader.result, file });
            reader.readAsDataURL(file);
          }),
      ),
    ).then((previews) => {
      setField({
        files: combined,
        previews: previews.map((p) => ({ name: p.name, src: p.src })),
      });
    });
  };

  const removeFileAt = (index) => {
    const nextFiles = [...(value.files || [])];
    const nextPreviews = [...(value.previews || [])];
    nextFiles.splice(index, 1);
    nextPreviews.splice(index, 1);
    setField({ files: nextFiles, previews: nextPreviews });
    setFileInputKey((k) => k + 1);
  };

  return (
    <Box sx={{ border: '1px solid #e6e6e6', borderRadius: '8px', p: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value.enabled)}
            onChange={(e) => setField({ enabled: e.target.checked })}
            disabled={disabled}
            sx={{ color: '#a3a3a3', '&.Mui-checked': { color: '#0E4D94' } }}
          />
        }
        label="Galeri ekle"
        sx={{ ml: 0, mb: value.enabled ? 2 : 0, '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 500 } }}
      />

      {value.enabled && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Galeri başlığı (opsiyonel)
            </Typography>
            <TextField
              fullWidth
              value={value.title || ''}
              onChange={(e) => setField({ title: e.target.value })}
              disabled={disabled}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Açıklama (opsiyonel)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={value.description || ''}
              onChange={(e) => setField({ description: e.target.value })}
              disabled={disabled}
              sx={fieldSx}
            />
          </Box>
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value.showOnDetailPage !== false}
                  onChange={(e) => setField({ showOnDetailPage: e.target.checked })}
                  disabled={disabled}
                  sx={{ color: '#a3a3a3', '&.Mui-checked': { color: '#0E4D94' } }}
                />
              }
              label="Detay sayfasında göster"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={value.showInGalleryPool !== false}
                  onChange={(e) => setField({ showInGalleryPool: e.target.checked })}
                  disabled={disabled}
                  sx={{ color: '#a3a3a3', '&.Mui-checked': { color: '#0E4D94' } }}
                />
              }
              label="Galeri havuzunda göster"
            />
          </Stack>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Görseller (opsiyonel, max {MAX_GALLERY_IMAGES})
            </Typography>
            <input
              key={`gallery-files-${fileInputKey}`}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              id="entity-gallery-files-upload"
              type="file"
              disabled={disabled || (value.files?.length || 0) >= MAX_GALLERY_IMAGES}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = '';
              }}
            />
            <label htmlFor="entity-gallery-files-upload">
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 2,
                  px: 2,
                  border: '2px dashed #e6e6e6',
                  borderRadius: '5px',
                  cursor: disabled ? 'default' : 'pointer',
                  backgroundColor: '#fafafa',
                  opacity: disabled ? 0.6 : 1,
                  '&:hover': disabled ? {} : { borderColor: '#0E4D94', backgroundColor: '#f5f5f5' },
                }}
              >
                <ImageIcon sx={{ fontSize: '20px', color: '#0E4D94' }} />
                <Typography variant="body2" sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500 }}>
                  Görsel Seç
                </Typography>
              </Box>
            </label>
            {(value.previews?.length || 0) > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#666', mb: 1 }}>
                  {value.files?.length || 0} / {MAX_GALLERY_IMAGES} görsel seçildi (ilk görsel kapak olur)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 1 }}>
                  {(value.previews || []).map((preview, idx) => (
                    <Box key={`${preview.name}-${idx}`} sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={preview.src}
                        alt={preview.name}
                        sx={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: '5px', border: '1px solid #e6e6e6' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeFileAt(idx)}
                        disabled={disabled}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: '#FFEEF3',
                          width: 24,
                          height: 24,
                          '&:hover': { backgroundColor: '#FFD6E0' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14, color: '#F8285A' }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default EntityGalleryFormSection;
